import { UserResponse } from '@acme/api';
import { Certificate, getUserCertificates } from 'crypto-pro';
import { useEffect, useState } from 'react';
import { getAuthSecret } from './lib';

interface UseCertificatesResult {
  certificates: Certificate[];
  certificatesError: ICertificatesError | null;
  certificate: Certificate | undefined;
  loading: boolean;
  selectCertificate: (thumbprint: string) => void;
  validCurrentCertificate: (user: UserResponse) => Certificate | undefined;
}

interface ICertificatesError {
  message: string;
  stack: string;
}

export const useCertificates = (
  callback?: (selectedCertificate: Certificate, secret: any) => void
): UseCertificatesResult => {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [certificatesError, setCertificatesError] =
    useState<ICertificatesError | null>(null);
  const [certificate, setCertificate] = useState<Certificate | undefined>(
    undefined
  );

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setCertificates(await getUserCertificates());
      } catch (error) {
        setCertificatesError(error as ICertificatesError);
      }
    })();
  }, []);

  const selectCertificate = async (thumbprint: string) => {
    setLoading(true);

    const selectedCertificate = certificates.find(
      ({ thumbprint: certThumbprint }) => certThumbprint === thumbprint
    );
    setCertificate(selectedCertificate);

    if (selectedCertificate) {
      try {
        const secretData = await getAuthSecret(selectedCertificate);

        if (callback) {
          callback(selectedCertificate, secretData!);
        }
      } catch (error) {
        console.error('Error:', error);
        setLoading(false);
        setCertificatesError(error as ICertificatesError);
      }
    }
  };

  const validCurrentCertificate = (user: UserResponse) => {
    const result: { [key in string]: string } = {};

    return certificates.find((c) => {
      const regex = /([\wА-ЯЁ\s]+)=([^,]+)/g;

      for (const [, key, value] of c.subjectName.matchAll(regex)) {
        (result as any)[key.trim()] = value;
      }

      return result?.E === user?.person?.email;
    });
  };

  return {
    certificates,
    certificatesError,
    certificate,
    loading,
    selectCertificate,
    validCurrentCertificate,
  };
};
